import type { TaskFilter } from './filter'
import type { ProjectViewPreferences } from '@/store/ProjectViewPreferencesStore'

export type SavedFilterScope = 'project' | 'space' | 'global'
export type SavedFilterPermission = 'private' | 'workspace' | 'project'

export interface SavedFilter {
  id: string
  envId: string
  name: string
  description: string | null
  filterConfig: string // JSON-stringified TaskFilter[]
  layoutConfig: string | null // JSON-stringified Partial<ProjectViewPreferences>, null = no layout bound
  scope: SavedFilterScope
  scopeEntityId: string | null // projectId or spaceId; null for global
  permission: SavedFilterPermission
  ownerId: string
  icon: string | null
  sortOrder: string | null
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * Parse the JSON filterConfig string into TaskFilter[]
 */
export function parseSavedFilterConfig(config: string): Array<TaskFilter> {
  try {
    return JSON.parse(config) as Array<TaskFilter>
  } catch {
    return []
  }
}

/**
 * Serialize TaskFilter[] into a JSON string for storage
 */
export function serializeSavedFilterConfig(filters: Array<TaskFilter>): string {
  // Chip ids are per-session; leaving them out keeps ungrouped views
  // byte-identical to what older app versions saved and read.
  return JSON.stringify(filters.map(({ id: _id, ...rest }) => rest))
}

/**
 * Parse the JSON layoutConfig string into a Partial<ProjectViewPreferences>.
 * Returns null when there is no layout bound to the view (legacy saved filter)
 * or when parsing fails.
 */
export function parseSavedLayoutConfig(
  config: string | null,
): Partial<ProjectViewPreferences> | null {
  if (!config) return null
  try {
    return JSON.parse(config) as Partial<ProjectViewPreferences>
  } catch {
    return null
  }
}

/**
 * Serialize a ProjectViewPreferences snapshot into a JSON string for storage.
 */
export function serializeSavedLayoutConfig(
  prefs: Partial<ProjectViewPreferences>,
): string {
  return JSON.stringify(prefs)
}

/**
 * Structural equality between a stored layoutConfig string and the current
 * in-memory preferences, over the keys the view binds. A view saved without a
 * layout (or with only some of it, design 20a's switches) ignores the rest.
 */
export function isLayoutEqual(
  savedConfig: string | null,
  currentPrefs: Partial<ProjectViewPreferences> | null | undefined,
): boolean {
  const saved = parseSavedLayoutConfig(savedConfig)
  if (!saved) return true
  if (!currentPrefs) return false

  for (const key of Object.keys(saved)) {
    const typedKey = key as keyof ProjectViewPreferences
    if (
      JSON.stringify(saved[typedKey]) !==
      JSON.stringify(currentPrefs[typedKey])
    ) {
      return false
    }
  }
  return true
}

/**
 * Returns true when the current filter/layout state diverges from the saved
 * view it was hydrated from — i.e. the user has edited something.
 */
export function isSavedViewDirty(
  view: { filterConfig: string; layoutConfig: string | null },
  filters: Array<TaskFilter>,
  currentPrefs: Partial<ProjectViewPreferences> | null | undefined,
): boolean {
  if (serializeSavedFilterConfig(filters) !== view.filterConfig) return true
  return !isLayoutEqual(view.layoutConfig, currentPrefs ?? null)
}
